// Dil kodunu locale'e çevirme
const localeMap: Record<string, string> = {
  tr: 'tr-TR',
  en: 'en-US',
  de: 'de-DE',
  fr: 'fr-FR',
  es: 'es-ES',
  it: 'it-IT',
  nl: 'nl-NL',
  ar: 'ar-SA',
  ru: 'ru-RU',
  zh: 'zh-CN'
};

// Global locale değişkeni (store'dan güncellenir)
let currentLocale = 'tr-TR';

/**
 * Set the current locale for formatting functions
 * @param lang Language code (tr, en, de, etc.)
 */
export function setFormatterLocale(lang: string): void {
  currentLocale = localeMap[lang] || 'tr-TR';
}

/**
 * Get current locale
 */
export function getFormatterLocale(): string {
  return currentLocale;
}

/**
 * Get current language code (tr, en, de, etc.)
 */
export function getCurrentLanguage(): string {
  // Reverse lookup from locale to language code
  for (const [lang, locale] of Object.entries(localeMap)) {
    if (locale === currentLocale) return lang;
  }
  return 'tr';
}

/**
 * Get date picker format string based on current locale
 * Used for Element Plus date picker format prop
 * @returns Format string (e.g., "DD/MM/YYYY", "MM/DD/YYYY", "YYYY-MM-DD")
 */
export function getDatePickerFormat(): string {
  const lang = getCurrentLanguage();
  
  // Date format patterns by language (Element Plus format: DD, MM, YYYY uppercase)
  const formatMap: Record<string, string> = {
    tr: 'DD.MM.YYYY',
    en: 'MM/DD/YYYY',
    de: 'DD.MM.YYYY',
    fr: 'DD/MM/YYYY',
    es: 'DD/MM/YYYY',
    it: 'DD/MM/YYYY',
    nl: 'DD-MM-YYYY',
    ar: 'YYYY/MM/DD',
    ru: 'DD.MM.YYYY',
    zh: 'YYYY-MM-DD'
  };
  
  return formatMap[lang] || 'DD/MM/YYYY';
}

/**
 * Get date picker format string for VueDatePicker component
 * VueDatePicker uses different tokens: dd (day), MM (month), yyyy (year)
 * @returns Format string (e.g., "dd.MM.yyyy", "MM/dd/yyyy", "yyyy-MM-dd")
 */
export function getVueDatePickerFormat(): string {
  const lang = getCurrentLanguage();
  
  // Date format patterns by language (VueDatePicker format: dd, MM, yyyy)
  const formatMap: Record<string, string> = {
    tr: 'dd.MM.yyyy',
    en: 'MM/dd/yyyy',
    de: 'dd.MM.yyyy',
    fr: 'dd/MM/yyyy',
    es: 'dd/MM/yyyy',
    it: 'dd/MM/yyyy',
    nl: 'dd-MM-yyyy',
    ar: 'yyyy/MM/dd',
    ru: 'dd.MM.yyyy',
    zh: 'yyyy-MM-dd'
  };
  
  return formatMap[lang] || 'dd/MM/yyyy';
}

/**
 * Format a date string to a localized format (DD.MM.YYYY or locale-specific)
 * @param dateStr Date string in format YYYY-MM-DD
 * @param locale Optional locale override
 * @returns Formatted date string
 */
export function formatDate(dateStr: string, locale?: string): string {
  if (!dateStr) return '-';
  
  try {
    const date = new Date(dateStr);
    return new Intl.DateTimeFormat(locale || currentLocale, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      timeZone: 'Europe/Istanbul'
    }).format(date);
  } catch (e) {
    console.error('Error formatting date:', e);
    return '-';
  }
}

/**
 * Format a time string to a localized format (HH:MM)
 * @param timeStr Time string in format HH:MM
 * @returns Formatted time string
 */
export function formatTime(timeStr: string | null): string {
  if (!timeStr) return '';
  
  return timeStr;
}

// Zamanı formatlama fonksiyonu
export function formatTimeWithSecond(dateTimeString: string | Date | null): string {
  if (!dateTimeString) return '-';
  try {
    return new Date(dateTimeString).toLocaleTimeString(currentLocale, {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  } catch (e) {
    return '-';
  }
};

// Tarihi tam formatlama
export function formatFullDate(dateString?: string | null): string {
  if (!dateString) return '-';
  try {
    return new Date(dateString).toLocaleDateString(currentLocale, {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      weekday: 'long'
    });
  } catch (e) {
    return dateString || '-';
  }
};

/**
 * Format a number as currency
 * @param value Number to format
 * @param currency Currency code (default: TRY)
 * @returns Formatted currency string
 */
export function formatCurrency(value: number, currency = 'TRY'): string {
  if (value % 1 === 0) {
    return new Intl.NumberFormat(currentLocale, {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  } else {
    return new Intl.NumberFormat(currentLocale, {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  }
}

/**
 * Format a number with given decimals
 * @param value Number to format
 * @param decimals Number of decimal places (default: 2)
 * @returns Formatted number string
 */
export function formatNumber(value: number, decimals = 2): string {
  return new Intl.NumberFormat(currentLocale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  }).format(value);
}

/**
 * Format days as integer (no decimals if whole number)
 * @param value Number of days
 * @returns Formatted days string
 */
export function formatDays(value: number): string {
  if (value % 1 === 0) {
    // If it's a whole number, show as integer
    return Math.floor(value).toString();
  } else {
    // If it has decimals, show with 1 decimal place
    return new Intl.NumberFormat(currentLocale, {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1
    }).format(value);
  }
} 

/**
 * Format a date string to a localized format (DD.MM.YYYY HH:MM)
 * @param dateStr Date string in format YYYY-MM-DD HH:MM
 * @returns Formatted date string
 */
export function formatDateWithTime(dateStr: string): string {
  if (!dateStr) return '';

  try {
    const date = new Date(dateStr);
    return new Intl.DateTimeFormat(currentLocale, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Europe/Istanbul'
    }).format(date);
  } catch (e) {
    console.error('Error formatting date with time:', e);
    return dateStr;
  }
}

export const getDayName = (dayOfWeek: number): string => {
  // Create a date that falls on the given day of week
  const date = new Date(2024, 0, 7 + dayOfWeek); // Jan 7, 2024 is a Sunday
  return new Intl.DateTimeFormat(currentLocale, { weekday: 'long' }).format(date);
}

export const getShortDayName = (dayOfWeek: number): string => {
  const date = new Date(2024, 0, 7 + dayOfWeek);
  return new Intl.DateTimeFormat(currentLocale, { weekday: 'short' }).format(date);
}

export const getMonthName = (month: number): string => {
  const date = new Date(2024, month - 1, 1);
  return new Intl.DateTimeFormat(currentLocale, { month: 'long' }).format(date);
}


export const getDateOnly = (dateTimeStr: string): string => {
  if (!dateTimeStr) return ''
  
  // ISO format: 2025-08-03T09:07:46.021Z
  if (dateTimeStr.includes('T')) {
    return dateTimeStr.split('T')[0]
  }
  
  // Space format: 2025-08-03 09:07:46
  if (dateTimeStr.includes(' ')) {
    return dateTimeStr.split(' ')[0]
  }
  
  // Already date only: 2025-08-03
  if (dateTimeStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
    return dateTimeStr
  }
  
  // Try to parse and format
  try {
    const date = new Date(dateTimeStr)
    if (!isNaN(date.getTime())) {
      return date.toISOString().split('T')[0]
    }
  } catch (e) {
    // Ignore parsing errors
  }
  
  return dateTimeStr
}

export const getTimeOnly = (dateTimeStr: string, includeSeconds: boolean = true): string => {
  if (!dateTimeStr) return ''
  
  let timePart = ''
  
  // ISO format: 2025-08-03T09:07:46.021Z
  if (dateTimeStr.includes('T')) {
    try {
      const date = new Date(dateTimeStr)
      if (!isNaN(date.getTime())) {
        timePart = new Intl.DateTimeFormat(currentLocale, {
          hour: '2-digit',
          minute: '2-digit',
          second: includeSeconds ? '2-digit' : undefined,
          timeZone: 'Europe/Istanbul'
        }).format(date)
      } else {
        // Fallback: string split
        timePart = dateTimeStr.split('T')[1]
        timePart = timePart.split('.')[0] // Remove milliseconds
      }
    } catch (e) {
      // Fallback: string split
      timePart = dateTimeStr.split('T')[1]
      timePart = timePart.split('.')[0] // Remove milliseconds
    }
  }
  // Space format: 2025-08-03 09:07:46
  else if (dateTimeStr.includes(' ')) {
    timePart = dateTimeStr.split(' ')[1]
  }
  // Already time only: 09:07:46
  else if (dateTimeStr.match(/^\d{2}:\d{2}:\d{2}$/)) {
    timePart = dateTimeStr
  }
  // Try to parse and extract time
  else {
    try {
      const date = new Date(dateTimeStr)
      if (!isNaN(date.getTime())) {
        timePart = new Intl.DateTimeFormat(currentLocale, {
          hour: '2-digit',
          minute: '2-digit',
          second: includeSeconds ? '2-digit' : undefined,
          timeZone: 'Europe/Istanbul'
        }).format(date)
      }
    } catch (e) {
      // Ignore parsing errors
    }
  }
  
  if (!timePart) return dateTimeStr
  
  // Remove seconds if not requested
  if (!includeSeconds && timePart.includes(':')) {
    const parts = timePart.split(':')
    if (parts.length >= 2) {
      return `${parts[0]}:${parts[1]}`
    }
  }
  
  return timePart
}

export const calculateWorkDuration = (startTime: string, endTime: string): string => {
  if (!startTime || !endTime) return '-'
  
  try {
    // ISO string'leri direkt parse et (timezone otomatik düzeltilir)
    const start = new Date(startTime)
    const end = new Date(endTime)
    
    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
      return '-'
    }
    
    // Zaman farkı hesapla (milliseconds cinsinden)
    const diffMs = end.getTime() - start.getTime()
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
    const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60))
    
    if (diffHours > 0) {
      return `${diffHours}sa ${diffMinutes}dk`
    } else {
      return `${diffMinutes}dk`
    }
  } catch (e) {
    return '-'
  }
}